import { z } from 'zod';

export type UserRole = 'client' | 'admin';

export interface User {
  id: string;
  phone: string;
  name?: string;
  email?: string;
  isVerified: boolean;
  isActive?: boolean;
  role: UserRole;
  lastLogin?: string;
  createdAt?: string;
  preferences?: {
    steeringWheelCover?: boolean;
    seatCovers?: boolean;
    airFreshener?: boolean;
    floorCovers?: boolean;
    trashBag?: boolean;
    tissues?: boolean;
  };
  /** Whether the user has explicitly set their preferences (false for old
   *  customers registered before the feature existed). */
  preferencesSet?: boolean;
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

export interface TimeRange {
  minFeet: number;
  maxFeet: number;
  duration: number;
}

export interface CreateProductOrderDto {
  items: Array<{ productId: string; quantity: number }>;
  b2bKey?: string; // B2B market link key — required when an item is a B2B product
  customerName?: string;
  addressId?: string;
  block?: string;
  avenue?: string;
  street?: string;
  building?: string;
  landmark?: string;
  customerAddress?: string;
  governorateId: string;
  districtId: string;
  deliveryDate: string; // YYYY-MM-DD
  deliveryPeriodIndex: number;
  notes?: string;
  useWalletCredit?: boolean;
  walletCreditAmount?: number;
  paymentMethod: 'knet' | 'credit_card' | 'apple_pay' | 'wallet' | 'online';
}

export interface ProductCategory {
  id: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  index: number;
  isActive: boolean;
  market?: 'retail' | 'b2b'; // missing on older records = retail
}

export interface Product {
  id: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  brand?: string;
  categoryId: string;
  categoryNameEn: string;
  categoryNameAr: string;
  size?: string;
  descriptionEn: string;
  descriptionAr: string;
  featuresEn: string[];
  featuresAr: string[];
  price: number;
  sku: string;
  imageUrl?: string;
  stockQuantity: number;
  inStock: boolean; // derived server-side: stockQuantity > 0
  index: number;
  isActive: boolean;
  market?: 'retail' | 'b2b'; // missing on older records = retail
}

export interface Service {
  id: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  price: number;
  vehiclePricing?: {
    sedan: number;
    suv: number;
    boat?: number;
    cuv?: number;
    motorcycle?: number;
    caravan?: number;
    van?: number;
  };
  duration: number;
  isActive: boolean;
  iconName?: string;
  imageUrl?: string;
  category?: string;
  // Per-unit pricing fields for caravan/boat
  isPerUnitPricing?: boolean;
  minUnit?: number;
  maxUnit?: number;
  timeRanges?: TimeRange[];
  availableAddOns?: string[];
  isAddOn?: boolean;
  isGlobalAddOn?: boolean;
  addOnScope?: 'item' | 'order';
  allowQuantity?: boolean; // Add-on only: customer can pick how many to take
}

export interface Governorate {
  id: string;
  nameEn: string;
  nameAr: string;
  code: string;
  deliveryFee: number;
  isActive: boolean;
  orderIndex: number;
}

export interface District {
  id: string;
  governorateId: string;
  nameEn: string;
  nameAr: string;
  deliveryFee: number | null;
  isActive: boolean;
  orderIndex: number;
}

export type VehicleType = 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'van' | 'boat' | 'caravan';
export type OrderStatus = 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled';
export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'expired';
export type PaymentMethod = 'knet' | 'credit_card' | 'apple_pay' | 'wallet' | 'online' | 'cash';

export interface OrderResponseItem {
  productId?: string;
  sku?: string;
  serviceId?: string;
  serviceName: string;
  vehicleType?: VehicleType;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  unit?: number;
  addOns?: string[];
  addOnQuantities?: AddOnQuantities;
}

export interface Order {
  type?: 'service' | 'product';
  deliveryPeriodEn?: string;
  deliveryPeriodAr?: string;
  deliveryWindow?: string;
  id: string;
  userId: string;
  serviceId?: string;
  serviceName?: string;
  serviceNameEn?: string;
  serviceNameAr?: string;
  items?: OrderResponseItem[];
  subtotal?: number;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  addressId?: string;
  block?: string;
  avenue?: string;
  street?: string;
  building?: string;
  landmark?: string;
  governorateId?: string;
  governorateNameEn?: string;
  governorateNameAr?: string;
  districtId?: string;
  districtNameEn?: string;
  districtNameAr?: string;
  vehicleType: VehicleType;
  appointmentDate: string;
  appointmentDateTime?: string;
  appointmentTime: string;
  timeSlotId?: string;
  status: OrderStatus;
  price?: number;
  deliveryFee?: number;
  totalPrice?: number;
  couponId?: string | null;
  discountAmount?: number;
  paymentMethod?: PaymentMethod;
  paymentStatus?: PaymentStatus;
  paymentLink?: string;
  paymentHash?: string;
  walletCreditUsed?: number;
  cashAmountDue?: number;
  isRefunded?: boolean;
  refundAmount?: number;
  notes?: string;
  completedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  createdAt: string;
  updatedAt?: string;
  walletTransactionId?: string;
  reviewId?: string;
  review?: {
    rating: number;
    comment?: string;
  };
}

export interface Review {
  id: string;
  orderId?: string;
  userId?: string;
  rating: number;        // 1-5 stars
  comment?: string;
  customerName?: string;
  isPublished: boolean;  // false by default (requires admin approval)
  createdAt: string;
  updatedAt?: string;
}

export interface TimeSlot {
  id: string;
  date?: string;
  dateTime?: string;
  time: string;
  endTime: string;
  duration: number;
  capacity?: number;
  totalCapacity?: number;
  availableSpots?: number;
  bookedCount: number;
  serviceId?: string;
  isActive?: boolean;
  slotsNeeded?: number;
  consecutiveSlotsAvailable?: number;
  totalDuration?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface Address {
  id: string;
  userId: string;
  label: string;
  governorateId: string;
  governorate?: {
    id: string;
    nameEn: string;
    nameAr: string;
  };
  districtId: string;
  district?: {
    id: string;
    nameEn: string;
    nameAr: string;
  };
  block: string;
  avenue?: string;
  street: string;
  building: string;
  landmark?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateAddressDto {
  label: string;
  governorateId: string;
  districtId: string;
  block: string;
  avenue?: string;
  street: string;
  building: string;
  landmark?: string;
  isDefault?: boolean;
}

export interface UpdateAddressDto {
  label?: string;
  governorateId?: string;
  districtId?: string;
  block?: string;
  avenue?: string;
  street?: string;
  building?: string;
  landmark?: string;
  isDefault?: boolean;
}

export interface OrderItem {
  serviceId: string;
  vehicleType: 'sedan' | 'suv' | 'cuv' | 'motorcycle' | 'caravan' | 'boat' | 'van';
  quantity: number;
  unit?: number;      // Selected feet for per-unit pricing
  addOns?: string[];  // Array of add-on service IDs
  addOnQuantities?: AddOnQuantities; // How many of each add-on; missing means 1
}

/** How many of each add-on, keyed by add-on service ID. */
export type AddOnQuantities = Record<string, number>;

/** Quantity taken of one add-on; anything unset counts as a single one. */
export function getAddOnQuantity(quantities: AddOnQuantities | undefined, addOnId: string): number {
  const quantity = quantities?.[addOnId];
  return quantity && quantity > 0 ? quantity : 1;
}

export interface CartItem extends OrderItem {
  serviceName: string;
  serviceNameAr: string;
  unitPrice: number;
  subtotal: number;
  isPerUnitPricing?: boolean;
  duration?: number;
}

export const orderItemSchema = z.object({
  serviceId: z.string().min(1),
  vehicleType: z.enum(['sedan', 'suv', 'cuv', 'motorcycle', 'caravan', 'boat', 'van']),
  quantity: z.number().min(1).default(1),
  unit: z.number().optional(),
  addOns: z.array(z.string()).optional(),
  addOnQuantities: z.record(z.number().int().min(1)).optional()
});

export const createOrderSchema = z.object({
  items: z.array(orderItemSchema).min(1),
  timeSlotId: z.string().min(1),
  governorateId: z.string().min(1),
  districtId: z.string().min(1),
  customerName: z.string().optional(),
  customerAddress: z.string().optional(),
  notes: z.string().optional(),
  addressId: z.string().optional(),
  block: z.string().optional(),
  avenue: z.string().optional(),
  street: z.string().optional(),
  building: z.string().optional(),
  landmark: z.string().optional(),
  couponCode: z.string().optional(),
  useWalletCredit: z.boolean().optional(),
  walletCreditAmount: z.number().optional(),
  paymentMethod: z.enum(['knet', 'credit_card', 'apple_pay', 'wallet', 'online']),
  addOns: z.array(z.string()).optional(), // order-wide global add-ons (scope 'order')
  addOnQuantities: z.record(z.number().int().min(1)).optional(), // how many of each order-wide add-on
  // Shop-cart products delivered together with the wash visit
  cartProducts: z
    .array(z.object({ productId: z.string().min(1), quantity: z.number().int().min(1) }))
    .optional(),
  b2bKey: z.string().optional(), // B2B market link key, when a cart product is B2B
});

export const loginSchema = z.object({
  phone: z.string().min(8)
});

export const verifyOtpSchema = z.object({
  phone: z.string().min(8),
  otpCode: z.string().length(6)
});

export const updateProfileSchema = z.object({
  name: z.string().min(1).optional(),
  preferences: z.object({
    steeringWheelCover: z.boolean().optional(),
    seatCovers: z.boolean().optional(),
    airFreshener: z.boolean().optional(),
    floorCovers: z.boolean().optional(),
    trashBag: z.boolean().optional(),
    tissues: z.boolean().optional(),
  }).optional(),
});

export type CreateOrderDto = z.infer<typeof createOrderSchema>;
export type LoginDto = z.infer<typeof loginSchema>;
export type VerifyOtpDto = z.infer<typeof verifyOtpSchema>;
export type UpdateProfileDto = z.infer<typeof updateProfileSchema>;

// Review Types
export type ReviewRating = 1 | 2 | 3 | 4 | 5;

export interface Review {
  id: string;
  orderId: string;
  userId: string;
  rating: ReviewRating;
  comment?: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface ReviewResponse {
  id: string;
  orderId: string;
  rating: number;
  comment: string;
  isPublished: boolean;
  createdAt: string;
}

export interface SubmitReviewDto {
  rating: number;
  comment?: string;
}

export interface WalletBalance {
  balance: number;
  currency: string;
  isNew?: boolean;
}

export interface WalletExpiringSummary {
  totalExpiring: number;
  nearestExpiry: string | null;
  currency: string;
}

export interface WalletTransaction {
  id: string;
  type: 'CREDIT' | 'DEBIT';
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  reason: 'ORDER_REFUND' | 'ORDER_PAYMENT' | 'ADMIN_CREDIT' | 'ADMIN_DEBIT'
    | 'INITIAL_BALANCE' | 'WELCOME_BALANCE' | 'ORDER_CASHBACK'
    | 'GIFT_PURCHASE' | 'GIFT_REDEMPTION' | 'EXPIRED_CREDIT';
  description: string;
  orderId?: string;
  createdAt: string;
  currency: string;
  // Attached from the credit lot (CREDIT transactions only) for expiry display.
  expiresAt?: string;
  lotStatus?: 'ACTIVE' | 'EXPIRED';
}
